import * as React from "react";
import styled from "styled-components";
import type { TypeModalRailProps } from "../ModalTypes";
import {
  RAIL_BUTTON_SIZE,
  RAIL_OFFSET,
  RAIL_GAP,
} from "../../shared/constants";

/**
 * Container for floating action buttons displayed alongside the modal.
 *
 * The rail provides a vertical column of action buttons positioned to the right of the modal.
 * Includes a close button by default (omitted when the Modal sets `disableDismiss`), with
 * support for additional custom actions. Renders nothing when it has no children.
 *
 * Actions are rendered through the Modal's `actions` prop or by directly including
 * ModalAction components as children.
 *
 * @example
 * <ModalRail>
 *   <ModalAction actionType="close" aria-label="Close" iconName="x-outline" />
 *   <ModalAction aria-label="Expand" iconName="arrows-pointing-out" onClick={handleExpand} />
 * </ModalRail>
 */
const Rail = styled.div`
  position: absolute;
  top: ${RAIL_OFFSET}px;
  right: calc(-1 * (${RAIL_BUTTON_SIZE}px + ${RAIL_OFFSET}px));
  display: flex;
  flex-direction: column;
  gap: ${RAIL_GAP}px;
  z-index: 1;
`;

export const ModalRail: React.FC<TypeModalRailProps> = ({ children }) => {
  // When the rail has no renderable children (e.g. disableDismiss with no
  // actions), render nothing rather than an empty absolutely-positioned <Rail>.
  // Children.toArray filters out null/undefined/boolean entries (unlike
  // Children.count, which counts them), so a `{false && <X/>}` branch is
  // correctly treated as empty here.
  if (React.Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <Rail
      data-slot="modal-rail"
      data-qa-modal-rail
      aria-label="Modal quick actions"
    >
      {children}
    </Rail>
  );
};

ModalRail.displayName = "ModalRail";
